import styled, { css } from 'styled-components';
import { horizontalSpacing } from 'tabview/pagesView/constants';
import { fontSizeDefinitions_DEPRECATED } from '../../atomicComponents/fonts/fontSizeDefinitionsWithDefaultFont';
import { ComponentRepresentation } from '@ardoq/basic-components';
import { componentOperations } from '@ardoq/core';
import { NavigatorItemData } from 'tabview/pagesView/types';
import { useRef } from 'react';
import { scrollToViewComponent } from 'tabview/pagesView/actions';
import { dispatchAction } from '@ardoq/rxbeach';
import { useOnMount } from '@ardoq/hooks';
import { classes } from '@ardoq/common-helpers';
import { colors } from '@ardoq/design-tokens';
import { filterCache } from 'collections/filterCache';
import { sanitizeFilterColorClassName } from '@ardoq/color-helpers';

const INDENTATION = parseInt(horizontalSpacing.MEDIUM, 10);

const ComponentRepresentationContainer = styled.div`
  margin-right: ${horizontalSpacing.SMALL};
  margin-left: ${horizontalSpacing.SMALL};
  display: flex;
  align-items: center;
  justify-content: center;
  align-content: center;
  height: 36px;
  width: 18px;
`;

const NavigatorItemContainer = styled.div<{
  $indentationLevel: number;
  $shouldHighlight: boolean;
}>`
  overflow: hidden;
  display: flex;
  flex-direction: row;
  line-height: 36px;
  align-items: center;
  padding-left: ${props => props.$indentationLevel * INDENTATION}px;
  &:hover {
    background-color: ${colors.grey100};
    cursor: pointer;
  }
  ${props =>
    props.$shouldHighlight &&
    css`
      background-color: ${colors.grey100};
      cursor: pointer;
      transition: 0.2s;
    `}
`;

const ComponentName = styled.div`
  ${fontSizeDefinitions_DEPRECATED.MEDIUM}
  color: ${colors.grey950};
  overflow-wrap: break-word;
  max-width: 95%;
`;

type NavigatorItemProps = {
  itemData: NavigatorItemData;
  shouldHighlight: boolean;
  shouldDim: boolean;
  observeItem: (item: HTMLDivElement) => void;
  unobserveItem: (item: HTMLDivElement) => void;
};

const NavigatorItem = ({
  itemData,
  shouldHighlight,
  shouldDim,
  observeItem,
  unobserveItem,
}: NavigatorItemProps) => {
  const { component, componentType, treeDepth } = itemData;
  const componentId = component._id;
  const ref = useRef<HTMLDivElement | null>(null);
  useOnMount(() => {
    if (ref.current) {
      observeItem(ref.current);
    }
    return () => {
      if (ref.current) {
        unobserveItem(ref.current);
      }
    };
  });
  const componentRepresentationData = componentOperations.getRepresentationData(
    component,
    componentType
  );
  const filterColor = filterCache.getColor(componentId);
  const sanitizedFilterColor = filterColor
    ? sanitizeFilterColorClassName(filterColor)
    : undefined;
  return (
    <NavigatorItemContainer
      ref={ref}
      $indentationLevel={treeDepth}
      $shouldHighlight={shouldHighlight}
      id={componentId}
      onClick={
        shouldDim
          ? undefined
          : () => dispatchAction(scrollToViewComponent({ componentId }))
      }
      style={shouldDim ? { opacity: 0.3 } : undefined}
    >
      <ComponentRepresentationContainer
        className={classes(
          componentOperations.getCssClassNames(
            component,
            componentType,
            sanitizedFilterColor
          ),
          'skipContextUpdate'
        )}
      >
        <ComponentRepresentation
          isImage={componentRepresentationData.isImage}
          value={componentRepresentationData.value}
          icon={componentRepresentationData.icon}
        />
      </ComponentRepresentationContainer>
      <ComponentName
        className={classes(
          'component',
          'component-name',
          'skipContextUpdate',
          sanitizedFilterColor
        )}
      >
        {component.name}
      </ComponentName>
    </NavigatorItemContainer>
  );
};

export { NavigatorItem };
